<script lang="ts">
	import ProductCartCard from '$lib/components/product/ProductCartCard.svelte'
	import CartTotals from '$lib/components/cart/CartTotals.svelte'
	import { is_set_array } from '$lib/js/helpers'
	import {
		storeCartItems,
		storeCustomerToken,
		storeMiniCartModal,
		storeWishlists,
		storeActiveWishlist,
	} from '$lib/js/stores'
	import { getUserWishlists } from '$lib/js/supabase/wishlists'
	import { onDestroy, onMount } from 'svelte'
	import { page } from '$app/state'
	import { getCartProducts, synchronizeCart } from '$lib/js/cart'
	import { t } from 'svelte-i18n-lingui'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { PUBLIC_LOCAL } from '$env/static/public'

	const scope = getContextScope()

	let listenerAdded = false

	onMount(() => {
		if (!PUBLIC_LOCAL || parseInt(PUBLIC_LOCAL) !== 1) {
			// If it's an account page delay the getCart() function
			if (page.route.id?.includes('/[[account]]')) {
				setTimeout(() => {
					;(async () => {
						await synchronizeCart().then(async (info) => {
							$storeCartItems = await getCartProducts(info, { scope: scope })
						})
					})()
				}, 4000)
			} else {
				;(async () => {
					await synchronizeCart().then(async (info) => {
						;[$storeWishlists, $storeCartItems] = await Promise.all([
							getUserWishlists({ scope }, $storeCustomerToken),
							getCartProducts(info, { scope: scope }),
						])
						if (is_set_array($storeWishlists)) {
							const activeWishlist = $storeWishlists.find(
								(wishlist) => wishlist.status === 'active'
							)
							if (activeWishlist) {
								$storeActiveWishlist = activeWishlist
							}
						}
					})
				})()
			}
		}
	})

	// Handle back actions using popstate
	function handlePopState() {
		$storeMiniCartModal = false
	}

	onDestroy(() => {
		if (typeof window != 'undefined') {
			window.removeEventListener('popstate', handlePopState)
		}
	})

	$effect(() => {
		if ($storeMiniCartModal && !listenerAdded) {
			history.pushState(null, '', window.location.href)
			window.addEventListener('popstate', handlePopState)
			listenerAdded = true
		} else if (!$storeMiniCartModal && listenerAdded) {
			window.removeEventListener('popstate', handlePopState)
			listenerAdded = false
		}
	})
</script>

<div class="mini-cart {$storeMiniCartModal ? 'opened' : 'closed'}">
	<button
		aria-label="Close cart"
		class="mini-cart__bg"
		onclick={() => ($storeMiniCartModal = false)}
	></button>
	<div class="mini-cart__inner">
		<div class="mini-cart__wrapper">
			<div class="mini-cart__top">
				<h2 class="head-5">{$t`Cart`}</h2>
				<button
					aria-label="Close cart"
					onclick={() => ($storeMiniCartModal = false)}
					class="icon icon-cross"
				></button>
			</div>

			{#if is_set_array($storeCartItems)}
				<div class="mini-cart__items-wrapper">
					<div class="mini-cart__items">
						{#each $storeCartItems as product}
							<ProductCartCard {product} />
						{/each}

						<span class="shadow"></span>
					</div>
				</div>
			{:else}
				<div class="mini-cart__empty">
					<p>{$t`Your cart is empty`}</p>
				</div>
			{/if}
		</div>

		<CartTotals type="minicart" />
	</div>
</div>

<style lang="scss">
	.mini-cart {
		position: relative;
		z-index: 19;
		visibility: hidden;
		opacity: 0;
		transition: all linear 0.125s;

		&.opened {
			opacity: 1;
			visibility: visible;

			.mini-cart {
				&__inner {
					right: 0;
				}
			}
		}

		&__empty {
			padding: 30px 15px;
			border: 1px dashed var(--grey--b);
			color: var(--grey--b);
			margin-top: 20px;
			text-align: center;
			border-radius: 10px;
		}

		&__bg {
			height: 100vh;
			width: 100%;
			background: rgba(0, 0, 0, 0.3);
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			transition: all linear 0.125s;
		}

		&__inner {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: fixed;
			height: calc(100vh - 40px);
			height: calc(100dvh - 40px);
			right: -100%;
			top: 0;
			width: calc(100% - 30px);
			background: white;
			box-shadow: 0px 4px 20px 10px rgba(0, 0, 0, 0.12);
			padding: 20px 15px;
			transition: all linear 0.125s;

			@media (min-width: 400px) {
				margin-left: 20px;
				width: calc(100% - 70px);
				padding: 30px 25px;
				height: calc(100vh - 60px);
				height: calc(100dvh - 60px);
			}

			@media (min-width: 769px) {
				width: 380px;
				right: -430px;
			}
		}

		&__items {
			max-height: calc(100vh - 300px);
			max-height: calc(100dvh - 300px);
			overflow-y: scroll;
			-ms-overflow-style: none;
			scrollbar-width: none;
			margin: 0 -15px;
			padding: 15px 15px 0;

			&::-webkit-scrollbar {
				display: none;
			}

			&-wrapper {
				position: relative;
			}

			.shadow {
				pointer-events: none;
				position: absolute;
				top: 340px;
				top: calc(100vh - 325px);
				top: calc(100dvh - 325px);
				height: 40px;
				background: linear-gradient(rgba(255, 255, 255, 0%), rgba(255, 255, 255, 100%));
				width: 100%;
			}
		}

		&__top {
			display: flex;
			justify-content: space-between;
			align-items: center;

			h2 {
				color: var(--main);
				margin: 0 20px 0 0;
			}

			button.icon {
				color: var(--main);
				font-size: 20px;
			}
		}
	}
</style>
